<template>
  <div class="page-wrapper">
    <div class="stepper-header">
      <v-img alt="Step Logo" class="step2-image step-logo"/>
      <div class="reg-titre1" v-html="title"></div>
    </div>

    <div>
      <v-form ref="USER" lazy-validation>
        <div>
          <div
              id="register-field-container"
              :class="dialog ? 'p-0 p-md-3' : 'py-2'"
              style="width:300px;"
          >
            <user-mail
                ref="IDENTIFIANT"
                v-model:value="mail"
                :theme="theme"
                class="custom-fieldS1"
                :show-missing="showMissing"
                :required="true"
                :context-id="user.id"
                :read-only="false"
                :obj-type="objType"
                :fiche="fiche"
                :section-name="sectionName"
                @status="statusMail"
            />
          </div>

          <div v-if="verification">
            <div class="intro-text">
              <span class="intro">Merci </span>
              <span class="name">{{ user.prenom }}</span>
              <span class="intro">, nous allons maintenant créer votre compte employeur</span>
            </div>

            <div class="email-text" v-html="texteMailVerif"/>

            <edit-field
                ref="CODE"
                :value="code"
                style="width: 100px; padding-bottom: 16px;"
                class="custom-fieldS3"
                :over-ride-placeholder="overRidePlaceholder"
                :hide-label="true"
                :edit-field-info="codeFieldInfo"
                :required="true"
                :data-table="true"
                @updated="setCode"
            />

            <div class="codeNonRecuContainer">
              <span class="info">Code valable 5 minutes. </span>
              <span class="reset-code" @click="resetIdent">
                {{ delayReset ? resetTxt + ' ' + delayLabel : resetTxt }}
              </span>
            </div>
          </div>
        </div>
      </v-form>
    </div>
  </div>

  <div class="action-footer">
    <loader-button
        :label="precedentBtnTxt"
        :outlined="true"
        @callback="previous"
    />
    <loader-button
        :label="suivantBtnTxt"
        :loading="loading"
        @callback="next"
    />
  </div>
</template>

<script lang="ts">
import {defineComponent, type PropType} from "vue";
import CrudUser from '@/generated/cruds/CrudUser.ts';
import { Sections } from '@/generated/classes/AppSchema.ts';
import EditField from "@/framework/base/components/EditField.vue";
import {EditFieldTheme, EditFieldType, FieldInfo} from "@/framework/base/components/FieldInfo.ts";
import Ressource from "@/framework/ressource/classes/Ressource.ts";
import {
  AppButtons,
  AppCustoms,
  FwkSections,
  RessourceAppButton,
  RessourceAppCustom,
  RessourceFwkSection
} from "@/ressources/globalRessources.ts";
import SiretInscription, {SiretInscriptionAnswser} from "@/generated/classes/SiretInscription.ts";
import {Alerts} from "@/framework/base/helpers/Alerts.ts";
import {storeSession} from "@/generated/stores";

export default defineComponent({
  components: {
    EditField
  },
  extends:  CrudUser,
  props: {
    subscription: {
      type: Object as PropType<SiretInscription>,
      required: true
    },
  },
  data() {
    return {
      sectionName: Sections.USER,
      delayReset: false,
      delay: 0,
      delayLabel: '',
      delayRemaining: 0,
      overRidePlaceholder: '___-___',
      verification: false,
      codeVerification: '',
      loading: false,
    }
  },
  computed: {
    codeFieldInfo(): FieldInfo {
      const ressource = new Ressource();
      const type = EditFieldType.STRING;
      const precision = 2;
      const format = null;
      const mask = this.mask;
      const focus = null;
      const live = null;
      return new FieldInfo(ressource, type, precision, mask, format, focus, live, EditFieldTheme.OUTLINED);
    },
    resetTxt(): string {
      return RessourceFwkSection(FwkSections.CODE_NOT_RECEIVED).label;
    },
    texteMailVerif() {
      return RessourceAppCustom(AppCustoms.REGISTER_ENVOIE_CODE_VERIF).label + '<span class="email">' + this.mail + '</span>.';
    },
    title(): string {
      return RessourceAppCustom(AppCustoms.REGISTER_ETAPE2_LABEL).label;
    },
    suivantBtnTxt(): string {
      return RessourceAppButton(AppButtons.SUIVANT).label;
    },
    precedentBtnTxt(): string {
      return RessourceAppButton(AppButtons.PRECEDENT).label;
    },
    code(): string {
      return this.codeVerification;
    },
  },
  methods: {
    setCode(value: string) {
      this.codeVerification = value;
    },
    mask(value: string): string {
      return 'NNN-NNN';
    },
    resetIdent(): void {
      SiretInscription.resetCodeMail(this.subscription);
      this.setDelayReset();
    },
    setDelayReset() {
      this.delayReset = true;
      if (this.delay == 0) {
        this.delay = 2000;
      } else if (this.delay == 2000) {
        this.delay = 15000;
      } else {
        this.delay = this.delay * 2;
      }
      this.delayRemaining = this.delay;
      this.setDelayLabel();
      setTimeout(() => this.clearDelayReset(), 1000);
    },
    clearDelayReset() {
      this.delayRemaining -= 1000;
      if (this.delayRemaining > 0) {
        this.setDelayLabel();
        setTimeout(() => this.clearDelayReset(), 1000);
      } else {
        this.delayReset = false;
      }
    },
    setDelayLabel() {
      this.delayLabel = ' (' + (this.delayRemaining / 1000).toFixed(0) + ' s)';
    },
    validateEmail(): void {
      this.loading = true;
      this.subscription.code = this.code.replace(/[ _-]/g, '');
      SiretInscription.validateEmail(this.subscription)
          .then((answer: SiretInscriptionAnswser) => {
            if (answer && answer.response.verifyMail) {
              this.loading = false;
              this.subscription.verifyMail = answer.response.verifyMail;
              this.subscription.validMail = answer.response.validMail;
              this.verification = false;
            } else {
              this.loading = false;
            }
          }).catch(() => {
            this.loading = false;
          })
    },
    verifyEmail(): void {
      this.loading = true;
      SiretInscription.verifyEmail(this.subscription)
          .then((answer: SiretInscriptionAnswser) => {
            if (answer.dialog) {
              Alerts.message(answer.dialog);
            } else {
              storeSession.setToken(answer.token, 0);
              this.subscription.token = answer.token;
              this.verification = true;
            }
            this.loading = false;
          }).catch(() => {
            this.loading = false;
          })
    },
    previous() {
      this.$emit('previous');
    },
    next() {
      // Reset validation if email changed
      if (this.updatedAndValid && this.subscription.validMail) {
        this.subscription.validMail = false;
      }
      // 1. Email already validated and not changed → skip verification
      if (this.subscription.validMail && !this.updatedAndValid) {
        this.subscription.verifyMail = true;
        this.subscription.verifyNumero = false;
        return;
      }
      // 2. Email not validated and not in verification mode → send code
      if (!this.verification && this.checkFields()) {
        this.verifyEmail();
        return;
      }
      // 3. In verification mode → validate code
      if (this.verification && !this.$refs.CODE.touch()) {
        this.validateEmail();
        return;
      }
    }
  }
})
</script>

<style scoped>

.intro-text {
  font-size: 16px;
  font-weight: 500;
}

.intro {
  color: rgb(var(--v-theme-grey20));
}

.name {
  color: rgb(var(--v-theme-primary50));
}

.email-text {
  font-size: 14px;
  font-weight: 400;
  color: rgb(var(--v-theme-secondary20));
}

.email {
  font-size: 16px;
  font-weight: 400;
  color: rgb(var(--v-theme-information50));
}
.codeNonRecuContainer {
  display: flex;
  font-size: 12px;
  font-weight: 400;
  gap: 4px;
  padding-bottom: 16px;
}

.info {
  color: rgb(var(--v-theme-secondary50));
}

.reset-code {
  color: rgb(var(--v-theme-primary50));
  cursor: pointer;
  transition: color 0.3s, text-decoration 0.3s;
}

.reset-code:hover {
  text-decoration: underline;
  color: #c01f1b;
}
</style>
